import { useNavigate } from "react-router-dom";
import { ArrowLeft, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

const WedgeGrindGuidePage = () => {
  const navigate = useNavigate();

  const grinds = [
    {
      name: "F Grind (Full Sole)",
      icon: "F",
      color: "emerald",
      bounce: "Medium til høy (10-14°)",
      description: "Den mest tilgivende grinden. Full såle med moderat bounce.",
      bestFor: "De fleste forhold, gress i god stand, nybegynnere",
      playStyle: "Rette slag, lite håndleddsbevegelse",
      beginner: true
    },
    {
      name: "M Grind (Mid Sole)",
      icon: "M",
      color: "emerald",
      bounce: "Medium (8-12°)",
      description: "Moderat såle med god allsidighet. Balansert design.",
      bestFor: "De fleste forhold, allsidig spiller",
      playStyle: "Både rette og åpne slag fungerer",
      beginner: true
    },
    {
      name: "S Grind (Standard)",
      icon: "S",
      color: "blue",
      bounce: "Medium (10-12°)",
      description: "Klassisk design med moderat såle. God allroundkølle.",
      bestFor: "Normale forhold, gressbaner i god stand",
      playStyle: "Standard tilnærming til ballen",
      beginner: false
    },
    {
      name: "K Grind (High Bounce)",
      icon: "K",
      color: "orange",
      bounce: "Høy (12-14°)",
      description: "Bred såle med mye bounce. Forhindrer at klubben borer seg ned.",
      bestFor: "Myk sand, mykt gress, høye handicap",
      playStyle: "Rette slag, sveiper gjennom sanden",
      beginner: false
    },
    {
      name: "L Grind (Low Bounce)",
      icon: "L",
      color: "red",
      bounce: "Lav (6-8°)",
      description: "Liten såle med lite bounce. Krever presisjon.",
      bestFor: "Faste forhold, tørr sand, lave handicap",
      playStyle: "Åpne kølleblad, kreative slag",
      beginner: false
    },
    {
      name: "W Grind (Wide Sole)",
      icon: "W",
      color: "purple",
      bounce: "Høy (12-14°)",
      description: "Ekstra bred såle, maksimal tilgivelse.",
      bestFor: "Bunkers med myk sand, mykt gress",
      playStyle: "Rette slag, sveiper under ballen",
      beginner: false
    },
    {
      name: "D Grind (Dual Sole)",
      icon: "D",
      color: "amber",
      bounce: "Medium (10-12°)",
      description: "To nivåer på sålen for allsidighet.",
      bestFor: "Varierende forhold, kreative spillere",
      playStyle: "Både åpne og rette slag",
      beginner: false
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: "bg-emerald-100 border-emerald-300 text-emerald-900",
      blue: "bg-blue-100 border-blue-300 text-blue-900",
      orange: "bg-orange-100 border-orange-300 text-orange-900",
      red: "bg-red-100 border-red-300 text-red-900",
      purple: "bg-purple-100 border-purple-300 text-purple-900",
      amber: "bg-amber-100 border-amber-300 text-amber-900"
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div 
              className="flex items-center cursor-pointer" 
              onClick={() => navigate("/")}
              data-testid="logo-home-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_golfbazar/artifacts/d3dy0w6h_Skjermbilde%202025-12-20%20125648.png" 
                alt="GolfMarked Logo" 
                className="h-14 w-auto"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <Info className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-3">
            Guide til Wedge Grinds
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Forstå forskjellen mellom wedge-typer og finn riktig grind for ditt spill
          </p>
        </div>

        {/* What is a Grind? */}
        <Card className="mb-8 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl">Hva er en wedge grind?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              <strong>Grind</strong> refererer til formen på sålen (bunnen) av wedgen. Forskjellige grinds påvirker 
              hvordan køllen oppfører seg i ulike forhold og med ulike slagtyper.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Hvordan lese navnet (f.eks. K.12)</h4>
              <ul className="text-blue-800 space-y-2">
                <li><strong>Bokstaven (K):</strong> Grind-typen - beskriver sålens form og tilpasning</li>
                <li><strong>Tallet (12):</strong> Bounce-grader - vinkelen mellom sålen og bakken</li>
                <li><strong>Eksempel:</strong> "M.10" betyr M-grind med 10° bounce</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Beginner Recommendation */}
        <Alert className="mb-8 border-emerald-300 bg-emerald-50">
          <AlertDescription className="text-emerald-900">
            <strong>For nybegynnere:</strong> Vi anbefaler <strong>F-grind</strong> eller <strong>M-grind</strong> med 
            medium bounce (10-12°). Disse er mest tilgivende og fungerer i de fleste situasjoner. De er markert med 
            grønn farge nedenfor.
          </AlertDescription>
        </Alert>

        {/* Grind Types */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {grinds.map((grind, index) => (
            <Card 
              key={index} 
              className={`border-l-4 ${grind.beginner ? 'border-l-emerald-500' : 'border-l-gray-300'} bg-white shadow-lg`}
              data-testid={`grind-card-${grind.icon}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl flex items-center gap-3">
                    <span className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl border-2 ${getColorClasses(grind.color)}`}>
                      {grind.icon}
                    </span>
                    {grind.name}
                  </CardTitle>
                  {grind.beginner && (
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded-full">
                      Nybegynner
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-700">Bounce:</p>
                  <p className="text-gray-600">{grind.bounce}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">Beskrivelse:</p>
                  <p className="text-gray-600">{grind.description}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">Best for:</p>
                  <p className="text-gray-600">{grind.bestFor}</p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700">Spillestil:</p>
                  <p className="text-gray-600">{grind.playStyle}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bounce Explanation */}
        <Card className="mb-8 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-2xl">Hva er bounce?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-gray-700">
            <p>
              <strong>Bounce</strong> er vinkelen mellom fremkanten og bakkanten av sålen. Det er "puten" som hindrer 
              køllen i å grave seg ned i bakken.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">Lav Bounce (6-8°)</h4>
                <p className="text-sm text-red-800">For faste forhold og presise spillere. Krever god teknikk.</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Medium Bounce (10-12°)</h4>
                <p className="text-sm text-blue-800">Allsidig og tilgivende. Best for de fleste spillere.</p>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-2">Høy Bounce (12-14°)</h4>
                <p className="text-sm text-orange-800">For myke forhold og høye handicap. Forhindrer at klubben borer seg ned.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Tips */}
        <Card className="bg-amber-50 border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-900">💡 Raske tips</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-gray-700">
            <p>• <strong>Kjøper du brukt?</strong> Sjekk at grind-bokstaven og bounce-tallet er synlig på køllen</p>
            <p>• <strong>Usikker?</strong> Velg M-grind med 10-12° bounce - fungerer for de fleste</p>
            <p>• <strong>Flere wedges?</strong> Ha samme grind på alle for konsistent føling</p>
            <p>• <strong>Test før kjøp:</strong> Prøv wedgen i sand og rundt greenen hvis mulig</p>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="mt-8 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white">
          <CardContent className="py-8 text-center">
            <h3 className="text-2xl font-bold mb-3">
              Klar til å finne din perfekte wedge?
            </h3>
            <p className="mb-6 text-emerald-50">
              Søk etter wedges med riktig grind for ditt spill
            </p>
            <Button
              onClick={() => navigate("/?category=wedge")}
              variant="secondary"
              size="lg"
              className="bg-white text-emerald-700 hover:bg-gray-100"
              data-testid="browse-wedges-button"
            >
              Se wedges til salgs
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WedgeGrindGuidePage;
